/**
 * UsersPage Component
 * Admin-only page for user management with selection system and bulk actions
 * Now with server-side pagination
 */

import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { X, SlidersHorizontal } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { UserRow } from '../components/users/UserRow';
import { InviteModal } from '../components/users/InviteModal';
import { ChangeRoleModal } from '../components/users/ChangeRoleModal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../components/ui/Button';
import { BottomSheet } from '../components/ui/BottomSheet';
import { PillGroup } from '../components/ui/PillGroup';
import { Pagination } from '../components/ui/Pagination';
import { cn } from '../lib/utils';
import { usersService, type User } from '../services/users.service';
import { useAuth } from '../hooks/useAuth';
import { isHr, ROLE_LABELS } from '../lib/roles';
import type { Role } from '../types';
import { useToast } from '../hooks/useToast';
import { usePaginationParams } from '../hooks/usePaginationParams';
import { USER_STATUS_COLORS } from '../components/users/userStatusColors';
import { csvCell } from '../lib/csv';

type SelectionType = 'mixed' | 'uninvited_only' | 'active_only' | 'invited_only';

export const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Pagination state from URL
  const { params, setPage, setLimit, setSearch, setFilter, clearFilters, correctPageIfNeeded } = usePaginationParams({
    defaultLimit: 25,
    defaultSort: 'last_name:asc',
    scrollToTop: true,
  });

  // Local search input state (for debouncing)
  const [searchInput, setSearchInput] = useState(params.search);

  // Selection state
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number | string>>(new Set());

  // Status filter (client-side for now since backend doesn't support it)
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'invited' | 'invited_expired' | 'uninvited_member' | 'deactivated'>('');

  // Modal state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Mobile filter sheet
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);

  // Count active filters for the mobile Filters button badge
  const activeFilterCount = useMemo(
    () => (params.role ? 1 : 0) + (statusFilter ? 1 : 0),
    [params.role, statusFilter]
  );

  // Debounced search - update URL params after 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== params.search) {
        setSearch(searchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, params.search, setSearch]);

  // Clear selection when page or filters change
  useEffect(() => {
    setSelectedUserIds(new Set());
  }, [params.page, params.search, params.role, statusFilter]);

  // Fetch users with server-side pagination and filtering
  const { data: usersResponse, isLoading, isFetching, error } = useQuery({
    queryKey: ['users', params, statusFilter],
    queryFn: () => usersService.getPaginated({
      page: params.page,
      limit: params.limit,
      search: params.search || undefined,
      sort: params.sort,
      role: params.role as Role | undefined,
      status: statusFilter || undefined,
    }),
    placeholderData: keepPreviousData,
  });

  // Extract data and meta from response
  const users = usersResponse?.data ?? [];
  const meta = usersResponse?.meta ?? { page: 1, limit: 25, total: 0, total_pages: 1 };

  // Correct page if it exceeds total pages
  useEffect(() => {
    if (usersResponse?.meta) {
      correctPageIfNeeded(usersResponse.meta.total_pages);
    }
  }, [usersResponse?.meta, correctPageIfNeeded]);

  // Get user status helper function
  const getUserStatus = (user: User): 'active' | 'invited' | 'invited_expired' | 'uninvited_member' | 'deactivated' => {
    if (user.record_type === 'uninvited_member') return 'uninvited_member';

    // Check if user is deactivated (highest priority for users with accounts)
    if (user.deactivated_at) return 'deactivated';

    if (user.email_verified_at) return 'active';

    // Check if invitation was revoked (cancelled by admin)
    if (user.invitation_revoked_at) return 'invited_expired';

    if (user.invitation_token) {
      if (user.invitation_expires_at) {
        const now = new Date();
        const expiresAt = new Date(user.invitation_expires_at);
        if (now > expiresAt) return 'invited_expired';
      }
      return 'invited';
    }
    return 'uninvited_member';
  };

  // Note: Status filtering is now done server-side via the status query param

  // Selected users
  const selectedUsers = useMemo(() => {
    return users.filter((user) => selectedUserIds.has(user.id));
  }, [users, selectedUserIds]);

  // Selection type
  const selectionType: SelectionType = useMemo(() => {
    const hasInactive = selectedUsers.some((u) => u.record_type === 'uninvited_member');
    const hasActive = selectedUsers.some((u) => u.record_type === 'user' && !u.deactivated_at && u.email_verified_at);
    const hasInvited = selectedUsers.some((u) => u.record_type === 'user' && !u.deactivated_at && !u.email_verified_at && u.invitation_token);
    const hasDeactivated = selectedUsers.some((u) => u.deactivated_at);

    // Mixed selection if multiple types are present
    if ((hasInactive && hasActive) || (hasInactive && hasDeactivated) || (hasActive && hasDeactivated) ||
        (hasInvited && hasActive) || (hasInvited && hasDeactivated) || (hasInvited && hasInactive)) {
      return 'mixed';
    }

    if (hasInactive) return 'uninvited_only';
    if (hasInvited) return 'invited_only';
    return 'active_only';
  }, [selectedUsers]);

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  // Handle role filter change
  const handleRoleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as '' | 'admin' | 'user';
    setFilter('role', value || null);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchInput('');
    setStatusFilter('');
    clearFilters();
  };

  // Select all visible
  const handleSelectAll = () => {
    const visibleUserIds = users.map((u) => u.id);
    const allVisibleSelected = visibleUserIds.every((id) => selectedUserIds.has(id));

    const newSelection = new Set(selectedUserIds);
    if (allVisibleSelected) {
      visibleUserIds.forEach((id) => newSelection.delete(id));
    } else {
      visibleUserIds.forEach((id) => newSelection.add(id));
    }
    setSelectedUserIds(newSelection);
  };

  // Toggle user selection
  const handleToggleSelection = (userId: number | string) => {
    const newSelection = new Set(selectedUserIds);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUserIds(newSelection);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedUserIds(new Set());
  };

  // Helper: Get numeric user ID from User object
  const getNumericUserId = (user: User): number | null => {
    // Use user_id if available (for actual users), otherwise use id if it's numeric
    return user.user_id ?? (typeof user.id === 'number' ? user.id : null);
  };

  // Handle single user invitation (opens modal with user pre-selected)
  const handleSingleUserInvitation = (user: User) => {
    // Select the user
    setSelectedUserIds(new Set([user.id]));
    // Open the invite modal
    setIsInviteModalOpen(true);
  };

  // Cancel invitation mutation
  const cancelInvitationMutation = useMutation({
    mutationFn: (memberId: number) => usersService.cancelInvitation(memberId),
    onSuccess: () => {
      toast.success('Invitation cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to cancel invitation');
    },
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: (memberId: number) => usersService.resendInvitation(memberId),
    onSuccess: () => {
      toast.success('Invitation resent successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to resend invitation');
    },
  });

  // Role-change modal target (null = closed)
  const [roleModalUser, setRoleModalUser] = useState<User | null>(null);

  // Change role mutation
  const changeRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: Role }) =>
      usersService.updateRole(userId, role),
    onSuccess: async () => {
      toast.success('User role updated successfully');
      setRoleModalUser(null);
      await queryClient.refetchQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update user role');
    },
  });

  // Deactivate user mutation
  const deactivateUserMutation = useMutation({
    mutationFn: (userId: number) => usersService.deactivate(userId),
    onSuccess: async () => {
      toast.success('User deactivated successfully');
      await queryClient.refetchQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to deactivate user');
    },
  });

  // Reactivate user mutation
  const reactivateUserMutation = useMutation({
    mutationFn: (userId: number) => usersService.reactivate(userId),
    onSuccess: async (data) => {
      toast.success(data.member_restored
        ? 'User reactivated and member restored successfully'
        : 'User reactivated successfully');
      await queryClient.refetchQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reactivate user');
    },
  });

  // Handle deactivate click - shows native confirmation dialog
  const handleDeactivateClick = (user: User) => {
    const userName = user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.email;

    // TW-399: an Admin or HR Manager who is deactivated also loses that role
    const roleNote = user.role === 'admin' || user.role === 'hr'
      ? ` Their ${ROLE_LABELS[user.role]} role will be changed to User; after reactivation it has to be granted again.`
      : '';
    if (window.confirm(`Are you sure you want to deactivate ${userName}? They will not be able to log in until reactivated.${roleNote}`)) {
      const userId = getNumericUserId(user);
      if (userId) {
        deactivateUserMutation.mutate(userId);
      } else {
        toast.error('Invalid user ID');
      }
    }
  };

  // Handle reactivate click - shows native confirmation dialog
  const handleReactivateClick = (user: User) => {
    const userName = user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.email;

    if (window.confirm(`Are you sure you want to reactivate ${userName}? They will be able to log in again.`)) {
      const userId = getNumericUserId(user);
      if (userId) {
        reactivateUserMutation.mutate(userId);
      } else {
        toast.error('Invalid user ID');
      }
    }
  };

  // Roles this actor may assign: HR managers can grant HR; admins cannot (§1.2).
  const assignableRoles: Role[] = isHr(currentUser?.role)
    ? ['user', 'admin', 'hr']
    : ['user', 'admin'];

  // Confirm handler for the ChangeRoleModal.
  const handleChangeRoleConfirm = (newRole: Role) => {
    if (!roleModalUser) return;
    const userId = getNumericUserId(roleModalUser);
    if (userId) {
      changeRoleMutation.mutate({ userId, role: newRole });
    } else {
      toast.error('Invalid user ID');
    }
  };

  // Bulk send invitations
  const handleBulkSendInvitations = () => {
    const inactiveUsers = selectedUsers.filter((u) => u.record_type === 'uninvited_member');
    if (inactiveUsers.length === 0) {
      toast.error('No uninvited members selected');
      return;
    }
    setIsInviteModalOpen(true);
  };

  // Bulk resend invitations (for expired invitations)
  const handleBulkResendInvitations = async () => {
    const invitedUsers = selectedUsers.filter(
      (u) => u.record_type === 'user' && !u.email_verified_at && u.invitation_token && u.member_id
    );
    if (invitedUsers.length === 0) {
      toast.error('No invited users selected');
      return;
    }

    try {
      const results = await Promise.allSettled(
        invitedUsers.map((u) => usersService.resendInvitation(u.member_id!))
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;

      if (succeeded > 0) {
        toast.success(`Resent invitations to ${succeeded} member${succeeded !== 1 ? 's' : ''}`);
      }
      if (failed > 0) {
        toast.error(`Failed to resend ${failed} invitation${failed !== 1 ? 's' : ''} (may be on cooldown)`);
      }
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSelectedUserIds(new Set());
    } catch (error) {
      toast.error('Failed to resend invitations');
    }
  };

  // Bulk deactivate
  const handleBulkDeactivate = async () => {
    if (!window.confirm(`Are you sure you want to deactivate ${selectedUserIds.size} user(s)? Any Admin or HR Manager among them will be changed to User.`)) {
      return;
    }

    try {
      await Promise.all(Array.from(selectedUserIds).map((id) => usersService.deactivate(id as number)));
      toast.success(`${selectedUserIds.size} user(s) deactivated successfully`);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      clearSelection();
    } catch (error) {
      toast.error('Failed to deactivate users');
    }
  };

  // Bulk reactivate
  const handleBulkReactivate = async () => {
    if (!window.confirm(`Are you sure you want to reactivate ${selectedUserIds.size} user(s)?`)) {
      return;
    }

    try {
      await Promise.all(Array.from(selectedUserIds).map((id) => usersService.reactivate(id as number)));
      toast.success(`${selectedUserIds.size} user(s) reactivated successfully`);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      clearSelection();
    } catch (error) {
      toast.error('Failed to reactivate users');
    }
  };

  // Bulk export
  const handleBulkExport = async () => {
    try {
      // Helper function to format status label
      const formatStatus = (status: string): string => {
        const statusLabels: Record<string, string> = {
          'active': 'Active',
          'invited': 'Invited',
          'invited_expired': 'Invited (Expired)',
          'uninvited_member': 'Not Invited',
          'deactivated': 'Inactive'
        };
        return statusLabels[status] || status;
      };

      // Helper function to escape CSV field

      // Generate CSV header
      const headers = ['First Name', 'Last Name', 'Email', 'Role', 'Status'];
      const csvRows = [headers.join(',')];

      // Generate CSV rows for selected users
      for (const user of selectedUsers) {
        const status = getUserStatus(user);

        const row = [
          csvCell(user.first_name || ''),
          csvCell(user.last_name || ''),
          csvCell(user.email),
          csvCell(user.role),
          csvCell(formatStatus(status))
        ];
        csvRows.push(row.join(','));
      }

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download with UTF-8 BOM for proper encoding
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `users_export_${timestamp}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success message
      toast.success(`Successfully exported ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export users');
    }
  };

  // Render bulk action buttons
  const renderBulkActions = () => {
    if (selectedUserIds.size === 0) return null;

    const allDeactivated = selectedUsers.every((u) => u.deactivated_at);

    return (
      <div className="flex items-center space-x-2">
        {selectionType === 'uninvited_only' && (
          <>
            <Button onClick={handleBulkSendInvitations} size="sm">
              <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Send Invitations
            </Button>
          </>
        )}
        {selectionType === 'invited_only' && (
          <Button onClick={handleBulkResendInvitations} size="sm">
            <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Resend Invitations
          </Button>
        )}
        {selectionType === 'active_only' && (
          <>
            {allDeactivated ? (
              <Button onClick={handleBulkReactivate} size="sm">
                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Reactivate Users
              </Button>
            ) : (
              <Button onClick={handleBulkDeactivate} variant="secondary" size="sm">
                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Deactivate Users
              </Button>
            )}
          </>
        )}
        <Button onClick={handleBulkExport} variant="secondary" size="sm">
          <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export
        </Button>
      </div>
    );
  };

  // Calculate select all state
  const visibleUserIds = users.map((u) => u.id);
  const selectedVisibleCount = visibleUserIds.filter((id) => selectedUserIds.has(id)).length;
  const selectAllChecked = selectedVisibleCount > 0 && selectedVisibleCount === visibleUserIds.length;
  const selectAllIndeterminate = selectedVisibleCount > 0 && selectedVisibleCount < visibleUserIds.length;

  // Check if any filters are active
  const hasActiveFilters = params.search || params.role || statusFilter;

  if (error) {
    return (
      <Layout>
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-lg font-medium text-heading mb-2">Failed to load users</h3>
          <p className="text-meta mb-4">There was an error loading the users list.</p>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['users'] })}>
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Try Again
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-heading flex items-center">
              <svg className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Users Management
            </h1>
          </div>
        </div>

        {/* Filters - Desktop (>= lg) */}
        <div className="hidden lg:block bg-surface rounded-lg shadow p-4">
          <div className="flex items-end gap-8">
            <div className="flex-1 max-w-[18%]">
              <label htmlFor="search-users" className="block text-xs font-bold text-body mb-1">
                Search Users
              </label>
              <Input
                id="search-users"
                type="search"
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={handleSearchInputChange}
              />
            </div>
            <div className="flex-1 max-w-[15%]">
              <label htmlFor="role-filter" className="block text-xs font-bold text-body mb-1">
                Filter by Role
              </label>
              <Select
                id="role-filter"
                value={(params.role as string) || ''}
                onChange={handleRoleFilterChange}
                options={[
                  { value: '', label: 'All Roles' },
                  { value: 'admin', label: 'Admin' },
                  { value: 'user', label: 'User' },
                  { value: 'hr', label: 'HR Manager' },
                ]}
              />
            </div>
            <div className="flex-1 max-w-[15%]">
              <label htmlFor="status-filter" className="block text-xs font-bold text-body mb-1">
                Filter by Account
              </label>
              <Select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'uninvited_member', label: 'Not Invited', color: USER_STATUS_COLORS.uninvited_member },
                  { value: 'invited', label: 'Invited', color: USER_STATUS_COLORS.invited },
                  { value: 'invited_expired', label: 'Invited (Expired)', color: USER_STATUS_COLORS.invited_expired },
                  { value: 'active', label: 'Active', color: USER_STATUS_COLORS.active },
                  { value: 'deactivated', label: 'Inactive', color: USER_STATUS_COLORS.deactivated },
                ]}
              />
            </div>
            <div className="ml-auto">
              <Button
                variant="tertiary"
                onClick={handleClearFilters}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Filters - Mobile (< lg) */}
        <div className="lg:hidden bg-surface rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0">
              <label htmlFor="search-users-mobile" className="block text-xs font-bold text-body mb-1">
                Search Users
              </label>
              <Input
                id="search-users-mobile"
                type="search"
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={handleSearchInputChange}
                className="text-base px-4 py-3 sm:text-sm sm:px-3 sm:py-2"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="tertiary"
                onClick={() => setFiltersSheetOpen(true)}
                className="flex items-center gap-2"
              >
                <SlidersHorizontal className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
              </Button>
              <Button variant="tertiary" onClick={handleClearFilters} className="flex items-center gap-2">
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile filters bottom sheet */}
        <BottomSheet
          isOpen={filtersSheetOpen}
          onClose={() => setFiltersSheetOpen(false)}
          title="Filters"
          footer={
            <div className="flex gap-6">
              <Button
                variant="secondary"
                onClick={handleClearFilters}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
              <Button
                variant="primary"
                onClick={() => setFiltersSheetOpen(false)}
                className="flex-1"
              >
                Apply Filters
              </Button>
            </div>
          }
        >
          <div className="space-y-6">
            <PillGroup
              label="Filter by Role"
              value={(params.role as string) || 'all'}
              options={[
                { value: 'all', label: 'All Roles' },
                { value: 'admin', label: 'Admin' },
                { value: 'user', label: 'User' },
                { value: 'hr', label: 'HR Manager' },
              ]}
              onChange={(value) =>
                handleRoleFilterChange({
                  target: { value: value === 'all' ? '' : value },
                } as React.ChangeEvent<HTMLSelectElement>)
              }
            />

            <div>
              <label className="block text-xs font-bold text-body mb-2">Filter by Account</label>
              <div className="border border-[var(--border-default)] rounded-md divide-y divide-[var(--border-default)]">
                {[
                  { value: '', label: 'All Status', dot: null },
                  { value: 'uninvited_member', label: 'Not Invited', dot: 'bg-gray-500' },
                  { value: 'invited', label: 'Invited', dot: 'bg-blue-500' },
                  { value: 'invited_expired', label: 'Invited (Expired)', dot: 'bg-orange-500' },
                  { value: 'active', label: 'Active', dot: 'bg-green-500' },
                  { value: 'deactivated', label: 'Inactive', dot: 'bg-red-500' },
                ].map((opt) => {
                  const selected = statusFilter === opt.value;
                  return (
                    <button
                      key={opt.value || 'all'}
                      type="button"
                      onClick={() => setStatusFilter(opt.value as typeof statusFilter)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 text-sm text-left',
                        selected
                          ? 'bg-blue-600 text-white font-medium'
                          : 'text-body hover:bg-surface-hover'
                      )}
                    >
                      {opt.dot ? (
                        <span className={cn('h-2.5 w-2.5 rounded-full flex-shrink-0', opt.dot)} />
                      ) : (
                        <span className="h-2.5 w-2.5 flex-shrink-0" />
                      )}
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </BottomSheet>

        {/* Page Info */}
        {meta.total > 0 && (
          <div className="text-sm text-meta">
            Page {meta.page} of {meta.total_pages}{'\u00A0\u00A0'}•{'\u00A0\u00A0'}{meta.total} users total
          </div>
        )}

        {/* Users List */}
        <div className="relative">
          {/* Loading overlay */}
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-surface/50 z-10 flex items-center justify-center">
              <LoadingSpinner size="md" />
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" text="Loading users..." />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 bg-surface rounded-lg shadow">
              <svg className="mx-auto h-12 w-12 text-meta mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <h3 className="text-lg font-medium text-heading mb-2">
                {hasActiveFilters ? 'No users found' : 'No users yet'}
              </h3>
              <p className="text-meta">
                {hasActiveFilters
                  ? 'Try adjusting your search or filters.'
                  : 'Invite members to create user accounts.'}
              </p>
            </div>
          ) : (
            <div className="bg-surface rounded-lg shadow divide-y divide-[var(--border-default)]">
              {/* List Header with Select All */}
              <div className="px-6 py-3 border-b border-[var(--border-default)] min-h-[58px] flex items-center">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectAllChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = selectAllIndeterminate;
                      }}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-strong rounded cursor-pointer"
                      title="Select All"
                      aria-label="Select all users"
                    />
                    <span className="text-sm text-meta">
                      {selectedVisibleCount} of {users.length} selected
                    </span>
                  </div>
                  {renderBulkActions()}
                </div>
              </div>

              {/* User Rows */}
              {users.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isSelected={selectedUserIds.has(user.id)}
                  onToggleSelection={() => handleToggleSelection(user.id)}
                  onSendInvitation={() => handleSingleUserInvitation(user)}
                  onCancelInvitation={() => user.member_id && cancelInvitationMutation.mutate(user.member_id)}
                  onResendInvitation={() => user.member_id && resendInvitationMutation.mutate(user.member_id)}
                  onChangeRole={() => setRoleModalUser(user)}
                  onDeactivate={() => handleDeactivateClick(user)}
                  onReactivate={() => handleReactivateClick(user)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {meta.total > 0 && (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.total_pages}
            entityName="users"
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={isFetching}
          />
        )}
      </div>

      {/* Invite Modal */}
      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => {
          setIsInviteModalOpen(false);
          // Clear selection after modal closes (for single-user invitations)
          clearSelection();
        }}
        users={selectedUsers.filter((u) => u.record_type === 'uninvited_member')}
      />

      {roleModalUser && (
        <ChangeRoleModal
          isOpen={!!roleModalUser}
          onClose={() => setRoleModalUser(null)}
          userName={
            roleModalUser.first_name && roleModalUser.last_name
              ? `${roleModalUser.first_name} ${roleModalUser.last_name}`
              : roleModalUser.email
          }
          currentRole={roleModalUser.role}
          allowedRoles={assignableRoles}
          onConfirm={handleChangeRoleConfirm}
          isLoading={changeRoleMutation.isPending}
        />
      )}
    </Layout>
  );
};
